import React, { PureComponent, memo } from 'react';
import PropTypes from 'prop-types';
import { connect, ReactReduxContext } from 'react-redux';
import classNames from 'classnames';
import {
  withGoogleMap,
  GoogleMap,
  Marker,
  Polyline,
  Polygon,
  InfoWindow,
  StreetViewPanorama,
  OverlayView,
  Circle
} from 'react-google-maps';
import { Map as Map2, fromJS, List } from 'immutable';
import { compose, withProps } from 'recompose';
import config from 'config';
import { MAP_NO_METRIC, searchParcel } from 'data/search';
import * as onKeyPress from 'utils/DOM/onKeyPress';
import SVG from 'components/base/SVG';
import Button from 'components/base/Button';
import withProperty from 'app/components/withProperty';
import PropertyInfo from './PropertyInfo';
import Analytics from './Analytics';
import css from './style.scss';
import { withRouter } from 'react-router-dom';
import DrawActive from '../../../assets/images/DrawActive.svg';
import DrawHover from '../../../assets/images/DrawHover.svg';
import PencilIcon from '../../../assets/images/PencilIcon.svg';
import iconClose from '../../../assets/images/iconCloses.svg';
import WhiteClose from '../../../assets/images/WhiteClose.svg';
import ReactDOM from 'react-dom';
import {
  boxCorners,
  boxFromCorners,
  circleFromEdgePoints,
  definitionKey,
  isUsableBox,
  pointsToShape,
  resizeShape,
  shapeBox,
  shapeFromBox,
  shapeFromOverlay,
  shapeToPoints,
} from './FreeDraw/shapeGeometry';

const {
  constants: {
    API: { BASE_URL },
  },
} = config;

const MIN_PARCEL_ZOOM = 19;

// Parts of the map container that aren't the map: our draw toolbar and Google's own controls.
const MAP_CONTROL_SELECTOR =
  '[data-draw-toolbar], .gmnoprint, .gm-control-active, .gm-svpc, .gm-fullscreen-control, .gm-style-mtc, .gm-bundled-control';
const emptyMap = Map2();

const drawProps = {
  strokeColor: '#128292',
  strokeWeight: 2,
  strokeOpacity: 0.8,
  clickable: false,
  fillColor: '#E1F8FA',
  fillOpacity: 0.3,
  geodesic: false
};

// Bounding box shown around a shape in Edit Shape mode.
const boxOutlineProps = {
  strokeColor: '#128292',
  strokeWeight: 1,
  strokeOpacity: 1,
  fillOpacity: 0,
  clickable: false,
  geodesic: false,
};

const dashedLineProps = {
  strokeOpacity: 0,
  clickable: false,
  icons: [{
    icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, strokeColor: '#128292', scale: 2 },
    offset: '0',
    repeat: '8px',
  }],
};

// Icons are built at render time, not import time: Google Maps loads with `defer`, so
// window.google.maps may not exist yet when this module is imported.

// Square corner handle (Figma: 8×8 px), centred on the corner: a 2×2 square, so `scale: 4` = 8 px.
const SQUARE_HANDLE_PATH = 'M -1 -1 L 1 -1 L 1 1 L -1 1 Z';

// Filled teal dot = the fixed corner while a corner is being dragged (Figma: a circle, drawn over
// that corner's square handle).
const fixedCornerIcon = () => ({
  path: window.google.maps.SymbolPath.CIRCLE,
  scale: 7,
  fillColor: '#128292',
  fillOpacity: 1,
  strokeColor: '#128292',
  strokeWeight: 2,
});

// Corner handles shown in Edit Shape mode (white square, teal border).
const editDotIcon = () => ({
  path: SQUARE_HANDLE_PATH,
  scale: 4,
  fillColor: '#FFFFFF',
  fillOpacity: 1,
  strokeColor: '#128292',
  strokeWeight: 2,
});

// Dotted diagonal from the fixed corner to the corner under the cursor (Figma: Edit Shape … Drag).
const DragDiagonal = ({ from, to }) => (
  <>
    <Polyline path={[from, to]} options={dashedLineProps} />
    <Marker position={from} icon={fixedCornerIcon()} clickable={false} zIndex={1001} />
  </>
);

DragDiagonal.propTypes = {
  from: PropTypes.object.isRequired,
  to: PropTypes.object.isRequired,
};

// Pencil-on-square icon for Edit Shape / Finish Editing (inherits the button's text colour).
const EditShapeIcon = () => (
  <svg className={css.editShapeIcon} width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <path d="M15.6724 2.32737C15.3349 1.98992 14.8771 1.80035 14.3998 1.80035C13.9225 1.80035 13.4648 1.98992 13.1272 2.32737L6.2998 9.15477V11.7H8.845L15.6724 4.87257C16.0099 4.53502 16.1994 4.07727 16.1994 3.59997C16.1994 3.12267 16.0099 2.66492 15.6724 2.32737Z" fill="currentColor" />
    <path d="M1.7998 5.39998C1.7998 4.92259 1.98945 4.46475 2.32701 4.12718C2.66458 3.78962 3.12242 3.59998 3.5998 3.59998H7.1998C7.4385 3.59998 7.66742 3.6948 7.8362 3.86358C8.00498 4.03236 8.0998 4.26128 8.0998 4.49998C8.0998 4.73867 8.00498 4.96759 7.8362 5.13637C7.66742 5.30515 7.4385 5.39998 7.1998 5.39998H3.5998V14.4H12.5998V10.8C12.5998 10.5613 12.6946 10.3324 12.8634 10.1636C13.0322 9.9948 13.2611 9.89998 13.4998 9.89998C13.7385 9.89998 13.9674 9.9948 14.1362 10.1636C14.305 10.3324 14.3998 10.5613 14.3998 10.8V14.4C14.3998 14.8774 14.2102 15.3352 13.8726 15.6728C13.535 16.0103 13.0772 16.2 12.5998 16.2H3.5998C3.12242 16.2 2.66458 16.0103 2.32701 15.6728C1.98945 15.3352 1.7998 14.8774 1.7998 14.4V5.39998Z" fillRule="evenodd" clipRule="evenodd" fill="currentColor" />
  </svg>
);

const parseShape = (def) =>
    def.split('/').map((p) => ({
      lat: Number(p.substr(0, p.indexOf(','))),
      lng: Number(p.substr(p.indexOf(',') + 1)),
    }));

const tileXYToQuadKey = (x, y, z) => {
  let quadKey = '';
  for (let i = z; i > 0; i--) {
    let digit = 0;

    const mask = 1 << (i - 1);
    if (x & mask) digit++;
    if (y & mask) digit += 2;

    quadKey = `${quadKey}${digit}`;
  }

  return quadKey;
};

// Updated formatValue to remove the '$' sign
function formatValue(val) {
  if (typeof val !== 'number' || isNaN(val)) return 'N/A';
  if (val >= 1_000_000_000)
    return `${(val / 1_000_000_000).toFixed(
        val < 10_000_000_000 ? 2 : val < 100_000_000_000 ? 1 : 0
    )}B`;
  if (val >= 1_000_000) {
    const mil = val / 1_000_000;
    return `${mil < 10 ? mil.toFixed(2) : mil.toFixed(1)}M`;
  }
  if (val >= 1_000) {
    const thou = val / 1_000;
    if (val < 10_000)
      return `${(Math.floor(thou * 100) / 100).toFixed(2)}K`;
    if (val < 100_000)
      return `${(Math.floor(thou * 10) / 10).toFixed(1)}K`;
    return `${Math.floor(thou)}K`;
  }
  return `${val}`;
}


const calcDims = (mini, sel, hov) => {
  const isOpenedOrHovered = sel;
  if (mini) {
    // Number pins (mini)
    return {
      iconWidth: isOpenedOrHovered ? 43 : 30,
      iconHeight: isOpenedOrHovered ? 32 : 20,
    };
  } else {
    // Est value pins (normal)
    return {
      iconWidth: isOpenedOrHovered ? 68 : 48,
      iconHeight: isOpenedOrHovered ? 32 : 20,
    };
  }
};

const chooseFont = (mini, len, sel) => {
  if (!mini) return sel ? '16px' : '12px';
  if (len <= 3) return sel ? '16px' : '12px';
  if (len <= 6) return sel ? '10px' : '8px';
  return sel ? '8px' : '6px';
};

/* ------------------------------------------------------------------ */
/* ------ memoised marker to minimise re-renders -------------------- */
const MemoMarker = memo(
    ({
       position,
       label,
       zIndex,
       isHovered,
       isSelected,
       onOver,
       onOut,
       onClick,
       children,
       icon,
     }) => (
        <Marker
            position={position}
            optimized={false}
            icon={icon}
            label={label}
            zIndex={zIndex}
            onMouseOver={onOver}
            onMouseOut={onOut}
            onClick={onClick}
        >
          {children}
        </Marker>
    ),
    (prev, next) =>
        prev.isHovered === next.isHovered &&
        prev.isSelected === next.isSelected &&
        prev.zIndex === next.zIndex &&
        prev.label.text === next.label.text &&
        prev.label.className === next.label.className &&
        prev.label.fontSize === next.label.fontSize &&
        prev.icon.scaledSize.width === next.icon.scaledSize.width &&
        prev.icon.scaledSize.height === next.icon.scaledSize.height &&
        prev.children === next.children
);
/* ------------------------------------------------------------------ */

class Map extends PureComponent {
  static contextType = ReactReduxContext;

  constructor(props) {
    super(props);

    this.map = null;
    this.innerMap = null;
    this.mapContainer = null;
    this.currentLatLng = null;

    this.state = {
      bounds: '',
      drawing: false,
      drawCoords: [],
      cursorCoord: null,
      infoProperty: null,
      parcelProperty: null,
      metric: MAP_NO_METRIC,
      markersRendered: 0,
      renderTimer: null,
      hoveredMarkerId: null,
      streetViewVisible: false,
      streetViewPosition: null,
      hovered: false,
      drawMode: null,
      showDrawMenu: false,
      showToast: false,
      handfreeDrawMode: false,
      isHandfreedrawing: false,
      mapContainer: null,
      isFullMapView: false,
      // Finished boundary, kept as what it is (circle / rectangle / free) so it can be edited.
      shape: null,
      // Corner pressed and current cursor while dragging out a box (used by the box draw tools).
      boxAnchor: null,
      boxCursor: null,
      editing: false,
      shapeBeforeEdit: null,
      // Corner handle being dragged while editing: { index, latLng }.
      dragHandle: null,
      // The whole shape is being dragged to a new place while editing.
      movingShape: false,
    };

    // Key of the last point list sent to onDraw, so the echo back via shapeDefinition keeps the typed shape.
    this.emittedKey = null;
    // Shape and fixed corner at the start of a corner drag, so drag events don't compound.
    this.resizeStart = null;
    // Map clicks before this time are ignored: the click of the press that just finished a shape.
    this.ignoreMapClicksUntil = 0;

    this.defaultOptions = Object.freeze({
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      draggableCursor: '',
      draggable: true,
      tilt: 0,
    });
    this.drawOptions = Object.freeze({
      // ...this.defaultOptions,
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      draggableCursor: 'crosshair',
      draggable: false,
    });
    this.handfreeOptions = Object.freeze({
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      draggableCursor: `url(${PencilIcon}) 8 16, auto`,
      draggable: false,
    });
    this.circleOptions = Object.freeze({
      mapTypeControlOptions: {
        position: window.google.maps.ControlPosition.LEFT_BOTTOM,
      },
      draggableCursor: 'crosshair',
      draggable: false,
    });

    this.onRef = this.onRef.bind(this);
    this.zoomToFit = this.zoomToFit.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleRightClick = this.handleRightClick.bind(this);
    this.handleDrawBegin = this.handleDrawBegin.bind(this);
    this.handleDrawCancel = this.handleDrawCancel.bind(this);
    this.handleRemoveBoundary = this.handleRemoveBoundary.bind(this);
    this.handleDrawComplete = this.handleDrawComplete.bind(this);
    this.handleMouseMove = this.handleMouseMove.bind(this);
    this.handleMetricChange = this.handleMetricChange.bind(this);
    this.handleInfoClose = this.handleInfoClose.bind(this);
    this.handleSaveProperty = this.handleSaveProperty.bind(this);
    this.handleDrawIconEnter = () => this.setState({ hovered: true });
    this.handleDrawIconLeave = () => this.setState({ hovered: false });
    this.handleHandfreeDrawIconClick = () =>
        this.state.handfreeDrawMode ? this.handleHandfreedrawModeCancel() : this.handleHandfreedrawModeBegin();
    this.handleDrawIconClick = () => {
      this.setState(prev => {
        const nextShowDrawMenu = !prev.showDrawMenu;
        return {
          showDrawMenu: nextShowDrawMenu,
          showToast: nextShowDrawMenu ? prev.showToast : false,
        };
      });
    };
  }

  componentDidMount() {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
          this.onLocation,
          this.onLocationError,
          { timeout: 3000 }
      );
    }

    this.onEsc = () => {
      if (this.state.drawing) this.handleDrawCancel();
      if (this.state.handfreeDrawing) this.handleHandfreeDrawComplete();
      if (this.state.handfreeDrawMode) this.handleHandfreedrawModeCancel();
      // Circle/rectangle: Escape mid-drag drops the box being dragged (the tool stays active);
      // Escape with the tool armed cancels it, like the Cancel button.
      if (this.state.boxAnchor) this.setState({ boxAnchor: null, boxCursor: null });
      else if (this.isBoxTool(this.state.drawMode)) this.handleDrawCancel();
    };

    this.onEnter = () => {
      if (this.state.drawing) this.handleDrawComplete();
      if (this.state.handfreeDrawing) this.handleHandfreeDrawComplete();
    };

    onKeyPress.addListener('Escape', this.onEsc);
    onKeyPress.addListener('Enter', this.onEnter);

    if (this.props.onModelRender === true) {
      this.startMarkerAnimationWithDelay();
    }

    // Only maps that draw (onDraw) restore a shape on mount. Maps without draw tools (e.g. the
    // Gallery bird view, the property group map) receive the search's shapeDefinition through the
    // redux fallback and must not show it.
    if (this.props.onDraw && this.props.shapeDefinition && !this.props.hidePolygon) {
      this.setState({ shape: pointsToShape(this.props.shapeDefinition) });
    }
  }

  componentWillUnmount() {
    onKeyPress.removeListener('Escape', this.onEsc);
    onKeyPress.removeListener('Enter', this.onEnter);

    if (this.mapContainer) {
      this.mapContainer.onmousedown = null;
      this.mapContainer.onmouseleave = null;
    }
    document.removeEventListener('mouseup', this.handleMapMouseUp);

    if (this.mouseDownListener) this.innerMap.removeListener(this.mouseDownListener);
    if (this.mouseUpListener) this.innerMap.removeListener(this.mouseUpListener);

    if (this.state.renderTimer) cancelAnimationFrame(this.state.renderTimer);
    if (this.delayTimer) clearTimeout(this.delayTimer);

    if (this.handleFullscreenChange) {
      document.removeEventListener("fullscreenchange", this.handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", this.handleFullscreenChange);
      document.removeEventListener("msfullscreenchange", this.handleFullscreenChange);
    }
  }

  componentDidUpdate(prevProps) {
    const { properties, activeResult, reset } = this.props;
    const { infoProperty } = this.state;

    if (this.props.reset && !prevProps.reset) {
      this.setState({ infoProperty: null, parcelProperty: null }, () => {
        if (this.props.onResetComplete) {
          this.props.onResetComplete();
        }
      });
      // The search can keep its boundary through a reset (loading a saved search with the same shape,
      // a favourite tab, coming back from a property). Then only what's in progress is cancelled and
      // the finished shape stays as it is (a rectangle stays a rectangle, a circle keeps its centre and
      // radius); its shapeDefinition doesn't change, so the check below wouldn't redraw a cleared one.
      // Only when there's no shape on the map yet is it built from the points.
      const keptDef = this.props.shapeDefinition;
      if (keptDef && this.props.onDraw && !this.props.hidePolygon) {
        this.cancelDrawingInProgress();
        if (!this.state.shape) this.setState({ shape: pointsToShape(keptDef) });
      } else {
        this.handleDrawCancel();
      }
    }

    if (this.props.properties && (!prevProps.properties || this.props.properties.size > prevProps.properties.size)) {
      this.startMarkerAnimationWithDelay();
    }

    const def = this.props.shapeDefinition;
    if (prevProps.shapeDefinition && !def) {
      this.emittedKey = null;
      this.setState({ drawCoords: [], shape: null, editing: false, shapeBeforeEdit: null });
    } else if (def && !this.props.hidePolygon && prevProps.shapeDefinition !== def) {
      // Our own onDraw echoing back keeps the typed shape; anything else (saved search, comps
      // restore) only carries points and is rebuilt from them (pointsToShape: a circle we drew
      // becomes a circle again, the rest a free shape), which replaces the drawing-time overlay.
      if (definitionKey(def) !== this.emittedKey) {
        this.emittedKey = null;
        this.setState({
          drawCoords: [],
          shape: pointsToShape(def),
          editing: false,
          shapeBeforeEdit: null,
        });
      }
    } else if (this.props.hidePolygon && (this.state.drawCoords.length || this.state.shape)) {
      this.setState({ drawCoords: [], shape: null, editing: false, shapeBeforeEdit: null });
    } else if (!this.props.hidePolygon && prevProps.hidePolygon && def) {
      this.setState({ shape: pointsToShape(def) });
    }

    let infoProp = null;
    (properties || []).forEach((p) => {
      if (activeResult && activeResult !== prevProps.activeResult) {
        if (activeResult === p.get('id')) infoProp = p;
      } else if (infoProperty && infoProperty.get('id') === p.get('id'))
        infoProp = p;
    });

    if (!infoProp && activeResult) {
      infoProp = (properties || []).find((p) => p.get('id') === activeResult);
    }

    if (infoProp && infoProp !== infoProperty && this.props.autoOpenInfo) {
      this.setInfoProperty(infoProp);
    }

    const { errors } = this.props;
    if (errors && errors !== prevProps.errors) {
      this.handleDrawCancel();
    }

    this.zoomToFit();
  }

  clearInfoProperty = () => {
    this.setState({ infoProperty: null });
  };

  startMarkerAnimationWithDelay = () => {
    clearTimeout(this.delayTimer);
    this.setState({ markersRendered: 0 });
    this.delayTimer = setTimeout(this.startMarkerAnimation, 2000);
  };

  startMarkerAnimation = () => {
    if (this.state.renderTimer) cancelAnimationFrame(this.state.renderTimer);
    const { tempRangeTo, pageCount, pageIndex, pageSize = 250, properties } = this.props;
    if (tempRangeTo && pageIndex === pageCount) {
      this.state.markersRendered = 0;
    }
    let maxMarkers = properties?.size || 0;
    if (tempRangeTo && pageIndex === pageCount) {
      const rowsShownBefore = (pageIndex - 1) * pageSize + (this.props.tempRangeFrom ? this.props.tempRangeFrom - 1 : 0);
      maxMarkers = Math.max(0, tempRangeTo - rowsShownBefore);
    }
    this.setState(
        prev => ({ markersRendered: Math.min(prev.markersRendered, maxMarkers) }),
        () => {
          const step = () => {
            this.setState(prev => {
              const next = prev.markersRendered + 5;
              if (next >= maxMarkers) {
                return { markersRendered: maxMarkers, renderTimer: null };
              }
              const id = requestAnimationFrame(step);
              return { markersRendered: next, renderTimer: id };
            });
          };
          const id = requestAnimationFrame(step);
          this.setState({ renderTimer: id });
        }
    );
  };

  onRef(ref) {
    this.map = ref;

    if (!this.map) return;

    this.innerMap =
        this.map.context.__SECRET_MAP_DO_NOT_USE_OR_YOU_WILL_BE_FIRED;

    const mapDiv = this.innerMap.getDiv();

    if (!this.handleFullscreenChange) {
      this.handleFullscreenChange = () => {
        const isFull =
            document.fullscreenElement ||
            document.webkitFullscreenElement ||
            document.msFullscreenElement;

        this.setState({ isFullMapView: !!isFull });
      };

      document.addEventListener("fullscreenchange", this.handleFullscreenChange);
      document.addEventListener("webkitfullscreenchange", this.handleFullscreenChange);
      document.addEventListener("msfullscreenchange", this.handleFullscreenChange);
    }

    const setContainer = () => {
      const container =
          mapDiv.classList.contains("gm-style")
              ? mapDiv
              : mapDiv.querySelector(".gm-style");

      if (container) {
        if (!this.mapContainer) {
          // Use DOM events, but attach mouseup to document to catch releases outside the map
          container.onmousedown = this.handleMapMouseDown;
          document.addEventListener('mouseup', this.handleMapMouseUp);
          container.onmouseleave = this.handleMapMouseLeave;

          this.mapContainer = container;

          this.setState({ mapContainer: container }, () => {
            this.forceUpdate();
          });
        }
      } else {
        setTimeout(setContainer, 100);
      }
    };

    setContainer();

    setTimeout(this.zoomToFit, 1);
  }

  simplifyCoords = (coords, tolerance) => {
    if (coords.length <= 2) return coords;

    const getPerpendicularDistance = (pt, lineStart, lineEnd) => {
      const x = pt.lat(), y = pt.lng();
      const x1 = lineStart.lat(), y1 = lineStart.lng();
      const x2 = lineEnd.lat(), y2 = lineEnd.lng();

      const dx = x2 - x1;
      const dy = y2 - y1;

      if (dx === 0 && dy === 0) return 0;

      return Math.abs(dy * x - dx * y + x2 * y1 - y2 * x1) /
          Math.sqrt(dx * dx + dy * dy);
    };

    const rdp = (points) => {
      if (points.length < 3) return points;

      let maxDist = 0;
      let index = 0;

      for (let i = 1; i < points.length - 1; i++) {
        const dist = getPerpendicularDistance(
            points[i],
            points[0],
            points[points.length - 1]
        );
        if (dist > maxDist) {
          index = i;
          maxDist = dist;
        }
      }

      if (maxDist > tolerance) {
        const left = rdp(points.slice(0, index + 1));
        const right = rdp(points.slice(index));
        return [...left.slice(0, -1), ...right];
      }

      return [points[0], points[points.length - 1]];
    };

    return rdp(coords);
  };

  optimizePolygon = (coords) => {
    if (!coords || coords.length < 3) return coords;

    const MAX_POINTS = 40;

    // Remove exact duplicate consecutive points only — no zoom-dependent thinning
    // so zoomed-in shapes keep their fine detail going into the simplifier.
    const reduced = [coords[0]];
    for (let i = 1; i < coords.length; i++) {
      const prev = reduced[reduced.length - 1];
      const curr = coords[i];
      const dx = curr.lat() - prev.lat();
      const dy = curr.lng() - prev.lng();
      if (dx !== 0 || dy !== 0) reduced.push(curr);
    }

    // If already within limit, just close and return.
    if (reduced.length <= MAX_POINTS) {
      return this.closePolygon(reduced);
    }

    // Adaptive RDP: binary-search for the *minimum* tolerance that brings the
    // point count to ≤ MAX_POINTS.  This preserves the maximum possible shape
    // fidelity regardless of zoom level — a zoomed-in shape gets a tiny
    // tolerance; a zoomed-out shape gets a proportionally larger one.
    let lo = 0;
    let hi = 1;
    while (this.simplifyCoords(reduced, hi).length > MAX_POINTS) hi *= 2;

    for (let i = 0; i < 20; i++) {
      const mid = (lo + hi) / 2;
      if (this.simplifyCoords(reduced, mid).length > MAX_POINTS) {
        lo = mid;
      } else {
        hi = mid;
      }
    }

    return this.closePolygon(this.simplifyCoords(reduced, hi));
  };

  closePolygon = (points) => {
    if (points.length > 2) {
      const first = points[0];
      const last = points[points.length - 1];
      if (first.lat() !== last.lat() || first.lng() !== last.lng()) {
        return [...points, first];
      }
    }
    return points;
  };

  onLocation(location) {
    console.log(this, location);
  }

  onLocationError(err) {
    console.log(this, err);
  }

  setInfoProperty(infoProperty = null) {
    this.setState({ infoProperty, parcelProperty: null });
  }

  handleMarkerMouseOver = (id) => {
    if (this.state.hoveredMarkerId !== id)
      this.setState({ hoveredMarkerId: id });
  };

  handleMarkerMouseOut = () => {
    if (this.state.hoveredMarkerId !== null)
      this.setState({ hoveredMarkerId: null });
  };

  handleClick(ev) {
    const { searchParcel, parcelClickEnabled } = this.props;
    const { drawing, drawCoords, handfreeDrawing, handfreeDrawMode, drawMode, editing } = this.state;
    const ll = ev.latLng;

    // The click of the press that just finished a rectangle arrives after the mouse-up has already
    // closed the tool, so it would otherwise open a parcel popup over the new shape.
    if (Date.now() < this.ignoreMapClicksUntil) return;

    if (drawing) {
      this.setState({ drawCoords: [...drawCoords, ev.latLng] });
    } else if (this.isBoxTool(drawMode) || editing) {
      // The circle and rectangle tools finish on mouse-up; clicks while drawing or editing must not open parcels.
    } else if (!handfreeDrawing && !handfreeDrawMode && parcelClickEnabled && this.map.getZoom() >= MIN_PARCEL_ZOOM) {
      this.setState({ infoProperty: null, parcelProperty: null }, () => {
        searchParcel(ll.lat(), ll.lng(), ({ response }) => {
          let parcelProperty;
          if (response && response.id) {
            const { latitude, longitude, parcelBoundaryDefinition } = response;
            parcelProperty = fromJS(response).set('position', { lat: latitude, lng: longitude }).set('path', parseShape(parcelBoundaryDefinition));
          }
          this.setState({ parcelProperty });
        });
      });
    }
  }

  handleRightClick() {
    // if (this.state.drawing) this.handleDrawComplete();
    const { drawing, handfreeDrawing, isHandfreedrawing } = this.state;
    if (drawing) this.handleDrawComplete();
    if (handfreeDrawing) this.handleHandfreeDrawComplete();
    if (isHandfreedrawing) this.handleMouseUp();
  }

  handleDrawBegin() {
    this.setState({ drawing: true, drawCoords: [], cursorCoord: null })
  }

  handleRemoveBoundary() {
    this.handleDrawCancel();
    if (this.props.onRemoveBoundary) this.props.onRemoveBoundary();
  }

  handleDrawCancel() {
    this.cancelDrawingInProgress();
    // ...and remove the finished shape too.
    this.emittedKey = null;
    this.setState({ shape: null });
  }

  // Stops whatever is in progress (click-to-draw and handfree-pencil modes, a box being dragged out,
  // Edit Shape) without removing the finished shape. An unfinished edit goes back to the shape from
  // before it, which is the one the search ran on.
  cancelDrawingInProgress() {
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: true });
    }
    this.resizeStart = null;
    this.setState(({ editing, shapeBeforeEdit, shape }) => ({
      drawing: false,
      drawMode: null,
      drawCoords: [],
      cursorCoord: null,
      showDrawMenu: false,
      showToast: false,
      handfreeDrawMode: false,
      handfreeDrawing: false,
      isHandfreedrawing: false,
      shape: editing && shapeBeforeEdit ? shapeBeforeEdit : shape,
      boxAnchor: null,
      boxCursor: null,
      editing: false,
      shapeBeforeEdit: null,
      dragHandle: null,
      movingShape: false,
    }));
  }

  // Keep the finished shape on the map and hand its points to the page (search / comps).
  emitShape(shape) {
    const points = shapeToPoints(shape);
    this.emittedKey = points.join('/');
    if (this.props.onDraw) this.props.onDraw(points);
  }

  handleDrawComplete() {
    const { drawing, drawCoords } = this.state;
    const { onDraw } = this.props;
    if (drawing) {
      if (drawCoords.length < 3) {
        this.handleDrawCancel();
      } else {
        this.setState({ drawing: false, cursorCoord: null });
        onDraw(drawCoords.map(c => `${c.lat()},${c.lng()}`));
        // this.setState({ drawCoords: [] });
      }
    }
  }

  handleHandfreeDrawCancel() {
    if (this.state.handfreeDrawing) this.setState({ handfreeDrawing: false, drawCoords: [] })
  }

  handleHandfreeDrawComplete() {
    const { handfreeDrawing, drawCoords } = this.state;
    const { onDraw } = this.props;
    if (handfreeDrawing) {
      if (drawCoords.length < 3) {
        this.handleHandfreeDrawCancel();
      } else {
        // Simplify only for free draw
        const optimized = this.optimizePolygon(drawCoords);
        this.setState({ handfreeDrawing: false, cursorCoord: null });
        onDraw(optimized.map(c => `${c.lat()},${c.lng()}`));
      }
    }
  }

  handleHandfreedrawModeBegin() {
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: false });
    }
    this.setState({ handfreeDrawMode: true, drawCoords: [], cursorCoord: null, isHandfreedrawing: false });
  }

  handleHandfreedrawModeCancel() {
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: true });
    }

    this.setState({
      handfreeDrawMode: false,
      handfreeDrawing: false,
      isHandfreedrawing: false,
      drawCoords: [],
      cursorCoord: null,
      drawing: false,
      drawMode: null,
      showDrawMenu: false,
      showToast: false,
    });
  }

  handleMouseDown(ev) {
    const { handfreeDrawMode } = this.state;
    if (handfreeDrawMode && ev.button === 0 && this.currentLatLng) {
      this.setState({ isHandfreedrawing: true, drawCoords: [this.currentLatLng] });
    }
  }

  handleMouseUp() {
    const { isHandfreedrawing, drawCoords } = this.state;

    if (isHandfreedrawing && drawCoords.length >= 3) {
      this.finishFreeDraw(drawCoords);
    } else if (isHandfreedrawing) {
      this.setState({ isHandfreedrawing: false });
    }
  }

  finishFreeDraw(drawCoords) {
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: true });
    }
    const optimized = this.optimizePolygon(drawCoords);
    // optimizePolygon closes the path; the shape keeps it open (shapeToPoints closes it again).
    const shape = { type: 'free', path: optimized.slice(0, -1) };
    // Close the tool so Edit Shape is offered for the new shape.
    this.setState({
      handfreeDrawMode: false,
      isHandfreedrawing: false,
      cursorCoord: null,
      drawCoords: [],
      drawMode: null,
      showDrawMenu: false,
      showToast: false,
      shape,
    });
    this.emitShape(shape);
  }

  // The circle and rectangle tools both draw with press, drag, release.
  isBoxTool = mode => mode === 'circle' || mode === 'rectangle';

  // The shape being dragged out with the current tool. Rectangle: the press is one corner and the
  // cursor the opposite one. Circle (PRD): the press is a point on the circle's edge that stays
  // fixed, and the circle grows from it towards the cursor.
  draggedShape() {
    const { drawMode, boxAnchor, boxCursor } = this.state;
    const cursor = boxCursor || boxAnchor;
    return drawMode === 'circle'
      ? circleFromEdgePoints(boxAnchor, cursor)
      : shapeFromBox('rectangle', boxFromCorners(boxAnchor, cursor));
  }

  handleMapMouseDown = (ev) => {
    const { handfreeDrawMode, drawMode } = this.state;
    if (handfreeDrawMode && ev.button === 0 && this.currentLatLng) {
      ev.preventDefault();
      ev.stopPropagation();
      this.setState({ isHandfreedrawing: true, drawCoords: [this.currentLatLng] });
    } else if (this.isBoxTool(drawMode) && ev.button === 0 && this.currentLatLng
        // The draw toolbar and Google's own controls (zoom, map type, fullscreen, Street View
        // pegman) are inside the map container: a press or drag on them must not start a shape.
        && !(ev.target && ev.target.closest && ev.target.closest(MAP_CONTROL_SELECTOR))) {
      // Press anchors the first point (a corner of the rectangle, a point on the circle's edge),
      // drag towards the cursor, release.
      ev.preventDefault();
      ev.stopPropagation();
      this.setState({ boxAnchor: this.currentLatLng, boxCursor: this.currentLatLng });
    }
  }

  // Release while drawing a circle or rectangle: hand the dragged shape to the page.
  finishBoxShape() {
    const { boxAnchor } = this.state;
    if (!boxAnchor) return;

    const shape = this.draggedShape();
    if (!isUsableBox(shapeBox(shape))) {
      // A press without a drag (or a very small one): stay in the tool so the user can press and drag.
      this.setState({ boxAnchor: null, boxCursor: null });
      return;
    }

    // Swallow the click that belongs to this same press (it follows the mouse-up).
    this.ignoreMapClicksUntil = Date.now() + 500;
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: true });
    }
    this.setState({
      shape,
      boxAnchor: null,
      boxCursor: null,
      drawMode: null,
      showDrawMenu: false,
      showToast: false,
    });
    this.emitShape(shape);
  }

  handleMapMouseUp = (ev) => {
    const { isHandfreedrawing, drawCoords, drawMode } = this.state;

    if (this.isBoxTool(drawMode)) {
      // Only the left button that started the box finishes it; a right/middle release mid-drag doesn't.
      if (ev && ev.button !== 0) return;
      this.finishBoxShape();
      return;
    }

    if (isHandfreedrawing && drawCoords.length >= 3) {
      this.finishFreeDraw(drawCoords);
    } else if (isHandfreedrawing) {
      this.setState({ isHandfreedrawing: false });
    }
  }

  handleMapMouseLeave = () => {
    const { isHandfreedrawing, handfreeDrawMode } = this.state;
    if (!isHandfreedrawing && !handfreeDrawMode) return;

    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: true });
    }

    const nextState = {
      isHandfreedrawing: false,
      drawCoords: [],
      cursorCoord: null,
    };

    if (!handfreeDrawMode) {
      Object.assign(nextState, {
        handfreeDrawing: false,
        drawing: false,
        drawMode: null,
        showToast: false,
      });
    }

    this.setState(nextState);
  }

  handleMouseMove(ev) {
    const { drawing, drawCoords, handfreeDrawing, isHandfreedrawing } = this.state;

    this.currentLatLng = ev.latLng;

    if (drawing && drawCoords.length) this.setState({ cursorCoord: ev.latLng });
    if (handfreeDrawing) {
      const newCoords = [...drawCoords, ev.latLng];
      this.setState({ drawCoords: newCoords, cursorCoord: ev.latLng });
    }

    if (isHandfreedrawing) {
      const { drawCoords: coords } = this.state;
      const newCoords = [...coords, ev.latLng];
      this.setState({ drawCoords: newCoords, cursorCoord: ev.latLng });
    }
    if (this.state.boxAnchor) this.setState({ boxCursor: ev.latLng });
  }
  handleMetricChange(metric) {
    const overlays = this.innerMap.overlayMapTypes;
    if (overlays.length > 1) overlays.removeAt(1);
    if (metric !== MAP_NO_METRIC) overlays.push(new window.google.maps.ImageMapType({ getTileUrl: ({ x, y }, z) => `${BASE_URL}/resource/auth/ps4/map/tiles/${tileXYToQuadKey(x, y, z)}/statistics/${metric}` }));
    this.setState({ metric });
  }

  handleInfoClose = () => {
    this.setState({ infoProperty: null, parcelProperty: null });
    if (this.props.onCloseInfoWindow) this.props.onCloseInfoWindow();
  };

  zoomToFit() {
    const { properties, initialMapType } = this.props;
    const props = (properties || List()).filter(p => p.get('latitude'));
    if (!props.size) {
      this.setState({ bounds: null });
    } else {
      const bounds = new window.google.maps.LatLngBounds();
      props.forEach((p) => {
        bounds.extend({ lat: p.get('latitude'), lng: p.get('longitude') });
      });

      const boundsStr = bounds.toString();
      if (boundsStr !== this.state.bounds) {
        this.setState({ bounds: boundsStr }, () => {
          this.map.fitBounds(bounds);
          setTimeout(() => {
            if (initialMapType === 'roadmap' && this.map.getZoom() > 19) {
              this.innerMap.setZoom(19);
            }
          }, 10);
        });
      }
    }
  }

  handleSaveProperty = (propertyData) => {
    const { saveProperty } = this.props;
    saveProperty(null, propertyData);
  };

  handleStreetViewPositionChange = (e) => {
    const streetViewPosition = e.getCenter();
    this.setState({ streetViewPosition });
  };

  handleSelectDrawMode = (mode) => {
    // Every draw tool draws with a press-and-drag, so the map must not pan meanwhile.
    if (this.innerMap) {
      this.innerMap.setOptions({ draggable: mode !== 'free' && !this.isBoxTool(mode) });
    }

    this.emittedKey = null;
    this.resizeStart = null;
    this.setState({
      drawMode: mode,
      drawing: false,

      handfreeDrawMode: false,
      handfreeDrawing: false,
      isHandfreedrawing: false,
      boxAnchor: null,
      boxCursor: null,
      shape: null,
      editing: false,
      shapeBeforeEdit: null,
      dragHandle: null,
      drawCoords: [],
      cursorCoord: null,
      showDrawMenu: true,
      showToast: true,
    });

    if (mode === 'free') {
      this.handleHandfreedrawModeBegin();
    }
  };

  handleDrawOptionMouseEnter = (mode) => {
    if (this.state.drawMode !== null) {
      this.setState({ showToast: this.state.drawMode === mode });
    }
  };

  handleDrawOptionMouseLeave = () => {
    if (this.state.drawMode !== null) {
      this.setState({ showToast: true });
    }
  };

  // Live preview while dragging out a circle or rectangle: only the shape grows under the cursor
  // (the corner handles and dotted line appear only when resizing in Edit Shape mode).
  renderBoxPreview() {
    const { drawMode, boxAnchor } = this.state;
    if (!this.isBoxTool(drawMode) || !boxAnchor) return null;

    const shape = this.draggedShape();
    if (!isUsableBox(shapeBox(shape))) return null;

    if (drawMode === 'circle') {
      return <Circle center={shape.center} radius={shape.radius} options={drawProps} />;
    }
    return <Polygon path={boxCorners(shape)} options={drawProps} />;
  }

  /* ---------------------------- Edit Shape ---------------------------- */

  handleEditStart = () => {
    const { shape } = this.state;
    if (!shape) return;
    this.setState({ editing: true, shapeBeforeEdit: shape, showDrawMenu: false, showToast: false });
  };

  handleEditFinish = () => {
    const { shape, shapeBeforeEdit } = this.state;
    this.resizeStart = null;
    this.setState({ editing: false, shapeBeforeEdit: null, dragHandle: null, movingShape: false });
    // Search once, and only when the shape actually changed.
    if (shape && (!shapeBeforeEdit || shapeToPoints(shape).join('/') !== shapeToPoints(shapeBeforeEdit).join('/'))) {
      this.emitShape(shape);
    }
  };

  handleCornerDragStart = (index) => {
    const { shape } = this.state;
    const corners = boxCorners(shapeBox(shape));
    // Resize relative to the shape at drag start so repeated drag events don't compound.
    this.resizeStart = { shape, fixedCorner: corners[(index + 2) % 4] };
  };

  handleCornerDrag = (index, ev) => {
    if (!this.resizeStart) return;
    const { shape, fixedCorner } = this.resizeStart;
    this.setState({
      shape: resizeShape(shape, fixedCorner, ev.latLng),
      dragHandle: { index, latLng: ev.latLng },
    });
  };

  handleCornerDragEnd = () => {
    this.resizeStart = null;
    this.setState({ dragHandle: null });
  };

  // The shape's Google overlay (Circle / Polygon), read back when it has been dragged to a new place.
  handleShapeRef = (ref) => {
    this.shapeOverlay = ref;
  };

  // Moving the whole shape in Edit Shape (drag its centre area). Google drags the overlay itself;
  // the box and corner handles are hidden meanwhile and follow the shape's new place on release.
  handleShapeDragStart = () => {
    this.setState({ movingShape: true });
  };

  handleShapeDragEnd = () => {
    const { shape } = this.state;
    const moved = shape && this.shapeOverlay ? shapeFromOverlay(shape.type, this.shapeOverlay) : null;
    this.setState(moved ? { shape: moved, movingShape: false } : { movingShape: false });
  };

  renderShape() {
    const { shape, editing } = this.state;
    if (!shape) return null;

    const options = {
      ...drawProps,
      clickable: editing,
      // In edit mode the shape can be dragged to move it (the map doesn't pan) and resized from its
      // corner handles; no vertex editing (PRD out of scope).
      draggable: editing,
      editable: false,
      zIndex: 10,
    };
    const dragProps = {
      ref: this.handleShapeRef,
      onDragStart: this.handleShapeDragStart,
      onDragEnd: this.handleShapeDragEnd,
    };

    if (shape.type === 'circle') {
      return <Circle center={shape.center} radius={shape.radius} options={options} {...dragProps} />;
    }

    const path = shape.type === 'rectangle' ? boxCorners(shape) : shape.path;
    return <Polygon path={path} options={options} {...dragProps} />;
  }

  renderEditHandles() {
    const { shape, editing, dragHandle, movingShape } = this.state;
    if (!shape || !editing || movingShape) return null;

    // Edit mode: box outline + corner handles; while a corner is dragged, the dotted diagonal
    // runs from the fixed corner to the cursor (Figma: Edit Shape … Drag).
    const corners = boxCorners(shapeBox(shape));
    const resizing = dragHandle && this.resizeStart;
    return (
        <>
          <Polygon path={corners} options={boxOutlineProps} />
          {resizing && <DragDiagonal from={this.resizeStart.fixedCorner} to={dragHandle.latLng} />}
          {corners.map((corner, index) => (
              <Marker
                  key={`edit-handle-${index}`}
                  position={dragHandle && dragHandle.index === index ? dragHandle.latLng : corner}
                  icon={editDotIcon()}
                  draggable
                  cursor={index % 2 === 0 ? 'nwse-resize' : 'nesw-resize'}
                  zIndex={1000}
                  onDragStart={() => this.handleCornerDragStart(index)}
                  onDrag={ev => this.handleCornerDrag(index, ev)}
                  onDragEnd={this.handleCornerDragEnd}
              />
          ))}
        </>
    );
  }

  render() {
    const {
      properties,
      onDraw,
      infoEnabled,
      analyticsEnabled,
      initialZoom,
      initialMapType,
      propertySaved,
      loading,
      property,
      getProperty,
      getPropertyContact,
      rangeEnabled,
      selectionCount,
      onSelectionDetails,
      isStreetView,
      isMyProperties,
      isComparables,
      onModelRender
    } = this.props;

    const {
      drawing,
      drawCoords,
      cursorCoord,
      metric,
      infoProperty,
      parcelProperty,
      hoveredMarkerId,
      streetViewPosition,
      hovered,
      handfreeDrawing,
      handfreeDrawMode,
      isHandfreedrawing,
      drawMode
    } = this.state;

    const { shape, editing } = this.state;
    const hasBoundary =
        Boolean(shape) ||
        (drawCoords && drawCoords.length >= 3);

    let cursorPolyline;
    let drawPolygon;

    if (isHandfreedrawing && drawCoords.length > 0) {
      // While actively hand-free drawing, show the path as a polyline (like drawing on paper)
      cursorPolyline = [...drawCoords];
      if (cursorCoord) {
        cursorPolyline.push(cursorCoord);
      }
    } else if (drawing && drawCoords.length > 0 && cursorCoord) {
      // Regular click-based drawing
      if (drawCoords.length < 3) {
        drawPolygon = [...drawCoords, cursorCoord];
      } else {
        cursorPolyline = [drawCoords[drawCoords.length - 1], cursorCoord];
        cursorPolyline.push(drawCoords[0]);
        drawPolygon = [...drawCoords];
      }
    }

    // Show closed polygon after handfree drawing is complete
    if (!isHandfreedrawing && drawCoords.length >= 3 && !handfreeDrawMode) {
      drawPolygon = [...drawCoords];
    }
    // if (drawCoords.length) {
    //   if (drawing && cursorCoord) {
    //     cursorPolyline = [drawCoords[drawCoords.length - 1], cursorCoord];
    //     if (drawCoords.length > 1) {
    //       cursorPolyline.push(drawCoords[0]);
    //     }
    //   }
    //   if (drawCoords.length + (cursorCoord ? 1 : 0) > 2) {
    //     drawPolygon = [...drawCoords];
    //     if (drawPolygon.length < 3) {
    //       drawPolygon.push(cursorCoord);
    //     }
    //   }
    // }

    const ul = window.UserLocation;
    const firstProperty = properties?.get?.(0);
    const firstPropertyType = firstProperty?.get?.('type');
    const { isFullMapView } = this.state;

    const mapOptions = (handfreeDrawMode || isHandfreedrawing)
        ? this.handfreeOptions
        : drawing
            ? this.drawOptions
            : drawMode === 'circle' || drawMode === 'rectangle'
                ? this.circleOptions
                : this.defaultOptions;

    const isComparable = this.props.type?.get?.('type') === 'COMP';

    const drawControls =
        !onDraw || !this.state.mapContainer
            ? null
            : ReactDOM.createPortal(
                <div data-draw-toolbar style={{
                  position: 'absolute',
                  top: '10px',
                  left: '10px',
                  right: '60px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  zIndex: 9999,
                  pointerEvents: 'none',
                }}>

                  <div>
                    <div
                        className={classNames(css.drawPill, {
                          [css.active]:
                          this.state.drawMode !== null ||
                          this.state.handfreeDrawMode ||
                          this.state.isHandfreedrawing,
                        })}
                        role="button"
                        tabIndex={0}
                        aria-pressed={drawing}
                        onClick={this.handleDrawIconClick}
                        onKeyDown={(e) =>
                            (e.key === 'Enter' || e.key === ' ') && this.handleDrawIconClick()
                        }
                    >
                      <img src={DrawActive} alt="" aria-hidden="true" className={css.iconDefault} />
                      <img src={DrawHover} alt="" aria-hidden="true" className={css.iconActive} />
                      <span className={css.label}>Draw</span>
                    </div>

                    {this.state.showDrawMenu && (
                        <div className={css.drawMenu}>

                          <div
                              className={classNames(css.drawOption, { [css.active]: this.state.drawMode === 'free' })}
                              role="button"
                              tabIndex={0}
                              aria-label="Free draw"
                              title="Free draw"
                              onClick={() => this.handleSelectDrawMode('free')}
                              onMouseEnter={() => this.handleDrawOptionMouseEnter('free')}
                              onMouseLeave={this.handleDrawOptionMouseLeave}
                          >
                            <div className={css.freeDrawIcon}>
                              <SVG icon="iconDrawFreeStroke" width={8} height={16} />
                              <SVG icon="iconDrawFreePencil" width={20} height={20} />
                            </div>
                          </div>

                          <div
                              className={classNames(css.drawOption, { [css.active]: this.state.drawMode === 'rectangle' })}
                              role="button"
                              tabIndex={0}
                              aria-label="Rectangle draw"
                              title="Rectangle draw"
                              onClick={() => this.handleSelectDrawMode('rectangle')}
                              onMouseEnter={() => this.handleDrawOptionMouseEnter('rectangle')}
                              onMouseLeave={this.handleDrawOptionMouseLeave}
                          >
                            <SVG icon="iconDrawRectangle" width={18} height={18} className={css.drawShapeIcon} />
                          </div>

                          <div
                              className={classNames(css.drawOption, { [css.active]: this.state.drawMode === 'circle' })}
                              role="button"
                              tabIndex={0}
                              aria-label="Circle draw"
                              title="Circle draw"
                              onClick={() => this.handleSelectDrawMode('circle')}
                              onMouseEnter={() => this.handleDrawOptionMouseEnter('circle')}
                              onMouseLeave={this.handleDrawOptionMouseLeave}
                          >
                            <SVG icon="iconDrawCircle" width={20} height={20} className={css.drawCircleIcon} />
                          </div>

                        </div>
                    )}

                    {this.state.showToast && !hasBoundary && (
                        <div className={css.draw} style={isComparable ? {
                          position: 'fixed',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 99999,
                          pointerEvents: 'auto',
                          top: '10px',
                        } : {}}>
                          <div className={css.drawInner}>
                            <div className={css.text}>
                              Click, drag or tap on the map to draw a shape in the area you want to search.
                            </div>
                            <div
                                className={classNames(css.buttonCustom, css.cancel)}
                                onClick={this.handleDrawCancel}
                                style={{ pointerEvents: 'auto' }}
                            >
                              Cancel
                            </div>
                          </div>
                        </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '10px', pointerEvents: 'auto' }}>
                    {shape && !drawMode && !handfreeDrawMode && (
                        <button
                            type="button"
                            className={classNames(css.editShapeBtn, { [css.finishEditing]: editing })}
                            onClick={editing ? this.handleEditFinish : this.handleEditStart}
                            aria-pressed={editing}
                        >
                          <EditShapeIcon />
                          {editing ? 'Finish Editing' : 'Edit Shape'}
                        </button>
                    )}

                    {hasBoundary && (
                        <button
                            className={css.removeBoundaryBtn}
                            onClick={this.handleDrawCancel}
                            aria-label="Remove boundary"
                        >
                          <img src={iconClose} alt="" aria-hidden="true" className={`${css.iconClose} ${css.iconCloseDefault}`} />
                          <img src={WhiteClose} alt="" aria-hidden="true" className={`${css.iconClose} ${css.iconCloseWhite}`} />
                          Remove Boundary
                        </button>
                    )}

                    {!isFullMapView && analyticsEnabled && (
                        <Analytics metric={metric} onChange={this.handleMetricChange} />
                    )}
                  </div>

                </div >,
                this.state.mapContainer
            );

    return (
        <>
          <GoogleMap
              defaultZoom={initialZoom || ul.zoom}
              defaultMapTypeId={initialMapType}
              defaultCenter={{ lat: ul.latitude, lng: ul.longitude }}
              options={mapOptions}
              onClick={this.handleClick}
              onRightClick={this.handleRightClick}
              onMouseMove={this.handleMouseMove}
              ref={this.onRef}
          >
            {(properties || emptyMap)
                .filter((p) => p.get('latitude'))
                .slice(0, this.state.markersRendered)
                .map((p, i) => {
                  const id = p.get('id');
                  const info = infoEnabled && infoProperty?.get?.('id') === id;

                  const isSelected = infoProperty
                      ? infoProperty.get('id') === id
                      : this.props.activeResult === id;

                  const isHovered = hoveredMarkerId === id;
                  const anyMiniPinActive = this.props.allSelected || selectionCount || onSelectionDetails || onModelRender;
                  const useMiniPin = anyMiniPinActive || rangeEnabled;
                  const isSubjectProperty = isComparables && i === 0;

                  let labelText;
                  if (isMyProperties || (isComparables && !isSubjectProperty)) {
                    labelText = String(i + 1);
                  } else {
                    const estValue = p.get('estimatedValue');
                    const onMarket = p.get('mlsListingAmount');
                    const onMarketlistingAmount = p.get('listingAmount');
                    const marketStatus = p.get('onMarket');
                    const valueToDisplay = marketStatus ? (onMarket ?? (onMarketlistingAmount || estValue)) : estValue;
                    labelText = useMiniPin ? String(p.get('seq') || p.get('resultIndex') || ' ') : formatValue(valueToDisplay);
                  }

                  const zIndex = 100 +
                      (properties.size - i) +
                      (info ? 10000000 : 0) +
                      (isHovered ? 1000000000 : 0);

                  // Determine dimensions for the icon and label
                  const { iconWidth, iconHeight } = calcDims(useMiniPin, isSelected, isHovered);

                  // Street View Color Gradient Logic - REMOVED
                  const customPinStyle = {};
                  if (isStreetView && isSubjectProperty) {
                    customPinStyle.backgroundColor = '#CA1551';
                  }

                  const markerClass = classNames(css.mapPin, {
                    [css.mini]: useMiniPin,
                    [css.normal]: !useMiniPin,
                    [css.selected]: isSelected || isSubjectProperty,
                    [css.hovered]: isHovered,
                    [css.subjectProperty]: isSubjectProperty,
                    [css.streetViewPin]: isStreetView && !isSubjectProperty,
                  });

                  const fontSize = chooseFont(useMiniPin, labelText.length, isSelected || isSubjectProperty);

                  return (
                      <MemoMarker
                          key={id}
                          position={{ lat: p.get('latitude'), lng: p.get('longitude') }}
                          icon={{
                            url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
                            scaledSize: new window.google.maps.Size(iconWidth, iconHeight),
                            labelOrigin: new window.google.maps.Point(iconWidth / 2, iconHeight / 2.5),
                            anchor: new window.google.maps.Point(iconWidth / 2, iconHeight / 2.5),
                          }}
                          label={{
                            text: labelText,
                            color: '#FFFFFF',
                            fontSize,
                            fontWeight: 'bold',
                            fontFamily: '$font-inter',
                            className: markerClass,
                            style: { ...customPinStyle },
                          }}
                          zIndex={zIndex}
                          isHovered={isHovered}
                          isSelected={isSelected || isSubjectProperty}
                          onOver={() => this.handleMarkerMouseOver(id)}
                          onOut={this.handleMarkerMouseOut}
                          onClick={() => {
                            this.setInfoProperty(p);
                            if (this.props.onMarkerClick) {
                              this.props.onMarkerClick(p.get('id'));
                            }
                          }}
                      >
                        {info && (
                            <InfoWindow onCloseClick={this.handleInfoClose}>
                              <PropertyInfo
                                  info={infoProperty}
                                  type={infoProperty && infoProperty.get('type')}
                                  history={this.props.history}
                                  handleSaveProperty={this.handleSaveProperty}
                                  isSaved={propertySaved}
                                  loading={loading}
                                  property={property}
                                  getProperty={getProperty}
                                  getPropertyContact={getPropertyContact}
                                  onCloseClick={this.handleInfoClose}
                                  searchType={firstPropertyType}
                                  onNearbyClick={this.props.onNearbyClick}
                                  store={this.context.store}
                              />
                            </InfoWindow>
                        )}
                      </MemoMarker>
                  );
                })}

            {!cursorPolyline ? null : (
                <Polyline path={cursorPolyline} options={{ ...drawProps }} />
            )}
            {!shape && drawPolygon && (
                <Polygon path={drawPolygon} options={drawProps} />
            )}
            {this.renderShape()}
            {this.renderEditHandles()}
            {this.renderBoxPreview()}

            {this.state.streetViewVisible && this.state.streetViewPosition && (
                <StreetViewPanorama
                    defaultPosition={this.state.streetViewPosition}
                    visible={true}
                    onPositionChanged={this.handleStreetViewPositionChange}
                />
            )}

            {!parcelProperty ? null : (
                <>
                  <Polygon path={parcelProperty.get('path')} options={drawProps} />
                  <MemoMarker
                      key="parcel"
                      position={parcelProperty.get('position')}
                      zIndex={1000000 + (parcelProperty.get('estimatedValue') || 0)}
                      onClick={() => this.setInfoProperty(parcelProperty)}
                      icon={{
                        url: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=',
                        scaledSize: new window.google.maps.Size(82, 38),
                        anchor: new window.google.maps.Size(41, 43),
                        labelOrigin: new window.google.maps.Point(41, 38),
                      }}
                      label={{
                        text: formatValue(
                            parcelProperty.get('onMarket') ? parcelProperty.getIn(['mlsListing', 'amount']) : parcelProperty.get('estimatedValue')
                        ),
                        color: '#FFFFFF',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        className: classNames(css.mapPin, css.normal, css.selected),
                      }}
                  >
                    <InfoWindow onCloseClick={this.handleInfoClose}>
                      <PropertyInfo
                          info={parcelProperty}
                          history={this.props.history}
                          handleSaveProperty={this.handleSaveProperty}
                          isSaved={propertySaved}
                          loading={loading}
                          property={property}
                          getProperty={getProperty}
                          getPropertyContact={getPropertyContact}
                          onCloseClick={this.handleInfoClose}
                          searchType={firstPropertyType}
                          onNearbyClick={this.props.onNearbyClick}
                          store={this.context.store}
                      />
                    </InfoWindow>
                  </MemoMarker>
                </>
            )}
          </GoogleMap>

          {drawControls}
          {/* {!analyticsEnabled ? null : (
          <Analytics metric={metric} onChange={this.handleMetricChange} />
        )} */}

        </>
    );
  }
}

Map.propTypes = {
  onDraw: PropTypes.func,
  onRemoveBoundary: PropTypes.func,
  infoEnabled: PropTypes.bool,
  analyticsEnabled: PropTypes.bool,
  parcelClickEnabled: PropTypes.bool,
  initialZoom: PropTypes.number,
  initialMapType: PropTypes.string,
  onNearbyClick: PropTypes.func,
  inputFlag: PropTypes.bool,
  hidePolygon: PropTypes.bool,
  autoOpenInfo: PropTypes.bool,
  allSelected: PropTypes.bool,
  isStreetView: PropTypes.bool,
  isMyProperties: PropTypes.bool,
  isComparables: PropTypes.bool,
  onMarkerClick: PropTypes.func,
};

Map.defaultProps = {
  infoEnabled: false,
  analyticsEnabled: false,
  parcelClickEnabled: false,
  initialMapType: 'roadmap',
  hidePolygon: false,
  autoOpenInfo: true,
  isStreetView: false,
  isMyProperties: false,
  isComparables: false,
};

const MapDetails = withProperty(Map, () => ({}), {});
export default withRouter(
    connect(
        (state, ownProps) => ({
          shapeDefinition: ownProps.shapeDefinition !== undefined
              ? ownProps.shapeDefinition
              : state.getIn(['search', 'location', 'shapeDefinition'], null),
          isStreetView: state.getIn(['streetView', 'isVisible'], false),
          isMyProperties: state.getIn(['myProperties', 'isViewingMap'], false),
          isComparables: state.getIn(['property', 'isViewingComparables'], false),
	  errors: state.get('errors'),
        }),
        { searchParcel }
    )(
        compose(
            withProps({
              loadingElement: <div style={{ height: '100%' }} />,
              containerElement: <div style={{ height: '100%', width: '100%' }} />,
              mapElement: <div style={{ height: '100%' }} />,
            }),
            withGoogleMap
        )(MapDetails)
    )
);